/* eslint-disable no-unused-vars */
import { put, takeLatest, call, all, select } from 'redux-saga/effects';
import { get, isEmpty } from 'lodash';
import {
  addItemToCartDataSuccess,
  removeCartSuccess,
  removeCartFailure,
  removeCartItemSuccess,
  serviceFailure,
  updateCartSuccess,
  addItemToCartFailure,
  removeCartItemFailure,
  updateCartFailure,
  requestQuoteSuccess,
  bulkAddItemToCartSuccess,
  resetCartErrorMessage,
  requisitionSubmitSuccess,
  quoteDetailsFailure,
  shareCartSubmitSuccess,
  shareConfirmationSubmitSuccess,
} from './ShoppingCart.actions';
import {
  ADD_ITEM_TO_CART,
  UPDATE_CART,
  REMOVE_CART,
  REMOVE_CART_ITEM,
  REQUEST_QUOTE,
  BULK_ADD_ITEM_TO_CART,
  INVALID_SKU_ERROR,
  CREATE_REQUISITION,
  REQUISITION_SUBMIT_ERROR,
  VALIDATE_QUOTE_CODE,
  QUOTE_STATUS,
  QUOTE_NOT_FOUND_ERROR,
  QUOTE_NO_LONGER_OPEN_ERROR,
  SHARE_CART_SUBMIT,
  SHARE_CONFIRMATION_SUBMIT,
  LOAD_ACCOUNT_DATA,
} from './ShoppingCart.constants';
import API_URLS from '../../../constants/api/services';
import API, { loader } from '../../../utils/fetch';
import { POST, PATCH, DELETE } from '../../../constants';
import {
  setCartKey,
  getCartKey,
  createCartStatus,
  getDefaultPathVar,
  getSoldToId,
  cartNotFoundStatus,
  isEcommUserLoggedIn,
  getUserId,
  getQuoteDetailsResponse,
  getQuoteError,
  getAppChannel,
  getLocalCountryLanguage,
  isUserLoggedIn,
} from '../../../utils/utils';

import {
  LoadShoppingCartData,
  LoadShoppingCartDataSuccess,
  setGlobalErrorStatus,
  addQuoteDetailsData,
} from '../../../global/actions';
import {
  PUNCHOUT_SETUP,
  SET_STORAGE_CART_ID,
  SET_STORAGE_CART_GUID,
  CART_URL,
  QUOTE_CONFIRMATION_URL,
  USER_ID_KEY,
  SOLD_TO_KEY,
  CHECKOUT_URL,
  QUOTE_ID_KEY,
  FREIGHT_CHARGE_DETAILS,
} from '../../../global/constants';
import {
  sessionGetItem,
  removeItem,
  sessionRemoveItem,
  sessionSetItemValue,
} from '../../../utils/storage';
import {
  getUserStatus,
  loadShoppingCartDataForQuote,
  loadCheckoutSaga,
} from '../../../global/saga/index';
import { isFreightEnabled, getShippingInfo } from '../../../utils/utilFunctions';

const getErrorResponse = error => {
  try {
    return error.response.json().then(res => res);
  } catch (e) {
    return {};
  }
};

const clearCartKey = () => {
  removeItem(SET_STORAGE_CART_ID);
  removeItem(SET_STORAGE_CART_GUID);
};

export function* addItemToCart(action) {
  try {
    // handle Ecomm user that is logged In on another tab
    if (isEcommUserLoggedIn()) {
      yield getUserStatus();
    }
    const store = yield select();
    const userType = get(store, ['global', 'globalData', 'userDetails', 'userRole', 'role'], '');
    const guid = getCartKey();
    const createCart = createCartStatus(guid);
    const { isFromHeaderQuickOrder = false, isProductRecommendation = false } = action;
    const channel = getAppChannel();
    const { localeCountry, localeLanguage } = getLocalCountryLanguage();
    const fetchProps = {
      body: { products: [{ code: action.code, quantity: action.quantity || 1 }] },
      method: POST,
      credentials: 'include',
      mode: 'cors',
    };
    const options = {
      fetchProps,
      query: {
        successWithCart: true,
        fields: 'BASIC',
        createCart: userType === 'EPROC' ? false : createCart,
        channel,
        localeCountry,
        localeLanguage,
      },
      pathVars: { guid, ...getDefaultPathVar() },
      showLoader: !isFromHeaderQuickOrder,
      isNextAPICall: !isFromHeaderQuickOrder,
    };
    const res = yield call(API.fetch, API_URLS.addToCart, options);
    const error = get(res, ['errors'], []);
    const errors = get(error, ['0'], {});
    const cart = get(res, ['cart'], {});
    if (!isEmpty(cart)) {
      setCartKey(cart);
    }
    if (!isEmpty(errors)) {
      loader(false);
      yield put(addItemToCartFailure({ ...errors, invalidSKU: action.code }));
      const cartNotFound = cartNotFoundStatus(error);
      if (cartNotFound) {
        clearCartKey();
        yield put(LoadShoppingCartDataSuccess({}));
      }
    } else {
      yield put(
        addItemToCartDataSuccess({
          res,
          productSKU: action.code,
          quantity: action.quantity || 1,
          isProductRecommendation,
        })
      );
      yield put(LoadShoppingCartData({ isFromHeaderQuickOrder }));
    }
  } catch (error) {
    const errorResponse = yield getErrorResponse(error);
    const errors = get(errorResponse, ['errors'], {});
    const cartNotFound = cartNotFoundStatus(errors);
    if (cartNotFound) {
      clearCartKey();
      yield put(LoadShoppingCartDataSuccess({}));
    }
    const errorData = get(errors, ['0'], {});
    yield put(setGlobalErrorStatus({ error: errorData }));
    yield put(addItemToCartFailure({ error: errorData }));
  }
}

export function* updateCart(action) {
  const { entry, productName, quantity, sKUPageUrl } = action.data;
  try {
    const guid = getCartKey();
    const fetchProps = {
      body: action.data,
      method: PATCH,
      credentials: 'include',
      mode: 'cors',
    };
    const channel = getAppChannel();
    const { localeCountry, localeLanguage } = getLocalCountryLanguage();
    const options = {
      fetchProps,
      query: { channel, localeCountry, localeLanguage },
      pathVars: { guid, entry, ...getDefaultPathVar() },
      showLoader: true,
      isNextAPICall: true,
    };
    const data = yield call(API.fetch, API_URLS.updateCart, options);
    const error = get(data, ['errors'], []);
    const errors = get(data, ['errors', '0'], {});
    if (!isEmpty(errors)) {
      loader(false);
      yield put(updateCartFailure(errors));
      const cartNotFound = cartNotFoundStatus(error);
      if (cartNotFound) {
        clearCartKey();
        yield put(LoadShoppingCartDataSuccess({}));
      }
    } else {
      const itemUpdated = yield select(state => ({
        ...get(state, ['global', 'globalData', 'cart', 'entries'], []).find(
          value => value.entryNumber === entry
        ),
        quantity,
      }));
      yield put(LoadShoppingCartData());
      yield put(updateCartSuccess({ itemUpdated }));
      if (Number(quantity) === 0) {
        yield put(
          removeCartItemSuccess({
            removedProductName: productName,
            removedItemUrl: sKUPageUrl,
            itemRemoved: itemUpdated,
          })
        );
      }
    }
  } catch (error) {
    const errorResponse = yield getErrorResponse(error);
    const errors = get(errorResponse, ['errors'], {});
    const cartNotFound = cartNotFoundStatus(errors);
    if (cartNotFound) {
      clearCartKey();
      yield put(LoadShoppingCartDataSuccess({}));
    }
    yield put(serviceFailure(error));
  }
}

export function* removeCart(action) {
  try {
    const guid = getCartKey();
    const isWholeCart = action.isWholeCart || false;
    const fetchProps = {
      method: DELETE,
      credentials: 'include',
      mode: 'cors',
    };
    const channel = getAppChannel();
    const { localeCountry, localeLanguage } = getLocalCountryLanguage();
    const options = {
      fetchProps,
      query: { isWholeCart, channel, localeCountry, localeLanguage },
      pathVars: { guid, ...getDefaultPathVar() },
      showLoader: true,
    };
    const res = yield call(API.fetch, API_URLS.removeCart, options);
    const data = get(res, ['success'], false);
    const error = get(res, ['errors'], []);
    const errors = get(res, ['errors', '0'], {});
    if (!isEmpty(errors)) {
      const cartNotFound = cartNotFoundStatus(error);
      if (cartNotFound) {
        clearCartKey();
        yield put(LoadShoppingCartDataSuccess({}));
      }
    }
    if (!data) {
      yield put(removeCartFailure(data));
    } else {
      const store = yield select();
      const itemRemoved = yield get(store, ['global', 'globalData', 'cart', 'entries'], {});
      yield put(removeCartSuccess({ ...res, entries: itemRemoved }));
      yield put(LoadShoppingCartData());
    }
  } catch (error) {
    const errorResponse = yield getErrorResponse(error);
    const errors = get(errorResponse, ['errors'], {});
    const cartNotFound = cartNotFoundStatus(errors);
    if (cartNotFound) {
      clearCartKey();
      yield put(LoadShoppingCartDataSuccess({}));
    }
    yield put(removeCartFailure(error));
  }
}

export function* removeCartItemSaga(action) {
  const { entry, productName, sKUPageUrl } = action.data;
  try {
    const guid = getCartKey();
    const fetchProps = {
      method: DELETE,
      credentials: 'include',
      mode: 'cors',
    };
    const channel = getAppChannel();
    const { localeCountry, localeLanguage } = getLocalCountryLanguage();
    const options = {
      fetchProps,
      query: { channel, localeCountry, localeLanguage },
      pathVars: { guid, entry, ...getDefaultPathVar() },
      showLoader: true,
      isNextAPICall: true,
    };
    const data = yield call(API.fetch, API_URLS.removeCartItem, options);
    const error = get(data, ['errors'], []);
    const errors = get(data, ['errors', '0'], {});
    if (!isEmpty(errors)) {
      loader(false);
      yield put(removeCartItemFailure(errors));
      const cartNotFound = cartNotFoundStatus(error);
      if (cartNotFound) {
        clearCartKey();
        yield put(LoadShoppingCartDataSuccess({}));
      }
    } else {
      const itemRemoved = yield select(state =>
        get(state, ['global', 'globalData', 'cart', 'entries'], []).find(
          value => value.entryNumber === entry
        )
      );
      yield put(LoadShoppingCartData());
      yield put(
        removeCartItemSuccess({
          removedProductName: productName,
          removedItemUrl: sKUPageUrl,
          itemRemoved,
        })
      );
    }
  } catch (error) {
    const errorResponse = yield getErrorResponse(error);
    const errors = get(errorResponse, ['errors'], {});
    const cartNotFound = cartNotFoundStatus(errors);
    if (cartNotFound) {
      clearCartKey();
      yield put(LoadShoppingCartDataSuccess({}));
    }
    yield put(serviceFailure(error));
  }
}

export function* requestQuote(action) {
  const cartFoundStatus = yield loadShoppingCartDataForQuote();
  const store = yield select();
  const history = get(store, ['global', 'globalData', 'route', 'history'], undefined);
  if (cartFoundStatus) {
    loader(false);
    history.push(CART_URL);
    return;
  }
  const { localeCountry, localeLanguage } = getDefaultPathVar();
  try {
    const options = {
      fetchProps: {
        credentials: 'include',
        mode: 'cors',
        method: POST,
        body: action.body,
        headers: {
          language: localeLanguage,
          countryCode: localeCountry,
        },
      },
      pathVars: getDefaultPathVar(),
      showLoader: true,
    };
    const quotRes = yield call(API.fetch, API_URLS.requestQuote, options);
    const data = get(quotRes, ['data'], {});
    if (data) {
      yield put(requestQuoteSuccess(data));
      history.push(QUOTE_CONFIRMATION_URL);
    }
  } catch (error) {
    const errorResponse = yield getErrorResponse(error);
    const errors = get(errorResponse, ['errors'], {});
    const cartNotFound = cartNotFoundStatus(errors);
    if (cartNotFound) {
      clearCartKey();
      yield put(LoadShoppingCartDataSuccess({}));
    }
    yield put(serviceFailure(error));
  }
}

export function* bulkAddItemToCart(action) {
  try {
    const store = yield select();
    const history = get(store, ['global', 'globalData', 'route', 'history'], undefined);
    const userType = get(store, ['global', 'globalData', 'userDetails', 'userRole', 'role'], '');
    const guid = getCartKey();
    const createCart = createCartStatus(guid);
    const fetchProps = {
      body: action.formData,
      method: POST,
      credentials: 'include',
      mode: 'cors',
    };
    const channel = getAppChannel();
    const options = {
      fetchProps,
      query: {
        successWithCart: true,
        fields: 'BASIC',
        createCart: userType === 'EPROC' ? false : createCart,
        channel,
      },
      pathVars: { guid, ...getDefaultPathVar() },
      showLoader: true,
      skipDefaultConfig: true,
      skipRequestBodystringify: true,
    };
    const res = yield call(API.fetch, API_URLS.bulkUploadAddToCart, options, true);
    const errors = get(res, ['errors'], []);
    const cart = get(res, ['cart'], {});
    const cartModifications = get(res, ['cartModifications'], []);
    if (!isEmpty(cart)) {
      setCartKey(cart);
    }
    const persistGlobalError = !isEmpty(errors)
      ? { persistGlobalError: { code: INVALID_SKU_ERROR, ...errors } }
      : {};
    loader(false);
    if (!isEmpty(errors)) {
      const cartNotFound = cartNotFoundStatus(errors);
      if (cartNotFound) {
        clearCartKey();
        yield put(LoadShoppingCartDataSuccess({}));
      }
      yield put(resetCartErrorMessage());
    }
    if (isEmpty(errors)) yield put(bulkAddItemToCartSuccess({ products: cartModifications }));
    yield put(LoadShoppingCartData(persistGlobalError));
    history.push(CART_URL);
  } catch (error) {
    const errorResponse = yield getErrorResponse(error);
    const errors = get(errorResponse, ['errors'], {});
    const cartNotFound = cartNotFoundStatus(errors);
    if (cartNotFound) {
      clearCartKey();
      yield put(LoadShoppingCartDataSuccess({}));
    }
    const errorData = get(errors, ['0'], {});
    yield put(setGlobalErrorStatus({ error: errorData }));
  }
}

function getRequisitionBody(store) {
  const cartData = get(store, ['global', 'globalData', 'cart'], {});
  const body = { cart: cartData };
  const shippingAndHandlingValue = get(body, ['cart', 'totalShippingAndHandling', 'value'], 0);
  const sapWebUserId = get(store, ['global', 'globalData', 'userDetails', 'sapWebUserId'], '');
  if (!isEmpty(sapWebUserId)) {
    body.sapWebUserId = sapWebUserId;
  }
  if (isFreightEnabled()) {
    const freightChargeDetails = sessionGetItem(FREIGHT_CHARGE_DETAILS);
    const carrierName = get(freightChargeDetails, ['shippingCarrier', 'carrierName'], '');
    const { carrierNumber: partnerNumber, state: selectedState } = getShippingInfo();
    const name = get(freightChargeDetails, ['shippingCarrier', 'name'], '');
    const countryCode = get(freightChargeDetails, ['country'], '');
    const carrierInfo = { carrierName, partnerNumber, countryCode, name };
    if (!isEmpty(selectedState)) {
      body.selectedState = selectedState;
    }
    if (!isEmpty(carrierInfo)) {
      body.carrierInfo = carrierInfo;
    }
    if (!parseFloat(shippingAndHandlingValue)) {
      delete body.cart.totalShippingAndHandling;
    }
  } else if (shippingAndHandlingValue) {
    delete body.cart.totalShippingAndHandling;
  }
  return body;
}
function* saveRequisition() {
  try {
    const punchoutSetup = sessionGetItem(PUNCHOUT_SETUP);
    const redirectUrl = punchoutSetup?.redirectUrl;
    const sessionId = redirectUrl
      .split('/id/')[1]
      .split('?')[0]
      .slice(0, -1);
    const sId = sessionId || '';
    const fetchProps = {
      method: POST,
      credentials: 'include',
      mode: 'cors',
      body: { sessionId: sId },
    };
    const options = {
      fetchProps,
      query: {},
      pathVars: getDefaultPathVar(),
      showLoader: true,
    };
    yield call(API.fetch, API_URLS.saveRequisition, options);
  } catch (error) {
    if (error?.response) {
      const errorResponse = yield error.response.json();
      yield put(setGlobalErrorStatus({ error: errorResponse }));
    }
  }
}

export function* createRequisition() {
  try {
    const store = yield select();
    const body = getRequisitionBody(store);
    let showError = false;
    const punchoutSetup = sessionGetItem(PUNCHOUT_SETUP);
    if (punchoutSetup?.returnUrl) {
      const options = {
        fetchProps: {
          mode: 'cors',
          method: POST,
          body,
        },
        pathVars: {},
        showLoader: true,
      };
      const response = yield call(API.fetch, punchoutSetup.returnUrl, options);
      if (response?.redirectUrl) {
        const cartData = get(store, ['global', 'globalData', 'cart'], {});
        yield put(requisitionSubmitSuccess({ cart: cartData }));
        yield saveRequisition();
        // Removed Session and Local storage
        sessionRemoveItem(USER_ID_KEY);
        sessionRemoveItem(SOLD_TO_KEY);
        sessionRemoveItem(PUNCHOUT_SETUP);
        clearCartKey();
        // Redirect to punchout portal
        window.location.href = response.redirectUrl;
      } else {
        showError = true;
      }
    } else {
      showError = true;
    }
    if (showError) {
      yield put(setGlobalErrorStatus({ error: { code: REQUISITION_SUBMIT_ERROR } }));
      loader(false);
    }
  } catch (error) {
    yield put(setGlobalErrorStatus({ error: { code: REQUISITION_SUBMIT_ERROR } }));
    loader(false);
  }
}

export function* validateQuoteCode(action) {
  try {
    const {
      data: { quoteCode, field, disableRestrictions = false },
    } = action;

    const store = yield select();
    const history = get(store, ['global', 'globalData', 'route', 'history'], undefined);
    const soldToId = getSoldToId();
    const userId = getUserId();

    const { localeCountry, localeLanguage } = getLocalCountryLanguage();
    const fetchProps = {
      credentials: 'include',
      mode: 'cors',
    };
    const options = {
      fetchProps,
      query: {
        soldToId,
        userId,
        fields: field,
        countryCode: localeCountry,
        language: localeLanguage,
        disableRestrictions,
      },
      pathVars: { quoteId: quoteCode },
      showLoader: true,
      isNextAPICall: true,
    };
    const res = yield call(API.fetch, API_URLS.quoteDetails, options);
    const status = get(res, ['quotes', 'quoteStatus'], '');
    const error = get(res, ['errors'], []);
    const errors = get(error, ['0'], {});
    const quoteError = getQuoteError(error);
    if (!isEmpty(errors) && !isEmpty(quoteError)) {
      yield put(quoteDetailsFailure({ error: { code: QUOTE_NOT_FOUND_ERROR } }));
    } else if (status === QUOTE_STATUS.open) {
      res.errors = quoteError;
      const quoteResponse = getQuoteDetailsResponse(res);
      const quoteId = get(quoteResponse, ['quotes', 'quoteId'], '');
      sessionSetItemValue(QUOTE_ID_KEY, quoteId);
      yield put(addQuoteDetailsData(quoteResponse));
      history.push(CHECKOUT_URL);
    } else {
      yield put(quoteDetailsFailure({ error: { code: QUOTE_NO_LONGER_OPEN_ERROR } }));
    }
    loader(false);
  } catch (error) {
    const errorResponse = yield getErrorResponse(error);
    const errors = get(errorResponse, ['errors'], {});
    const errorData = get(errors, ['0'], {});
    yield put(setGlobalErrorStatus({ error: errorData }));
    loader(false);
  }
}

export function* shareCartSubmit(action) {
  try {
    const { data: body } = action;
    const fetchProps = {
      body,
      method: POST,
      credentials: 'include',
      mode: 'cors',
    };
    const options = {
      fetchProps,
      query: {},
      pathVars: getDefaultPathVar(),
      showLoader: true,
    };
    const res = yield call(API.fetch, API_URLS.shareCart, options);
    const error = get(res, ['errors', '0'], {});
    if (!isEmpty(error)) {
      yield put(setGlobalErrorStatus({ error }));
    }
    yield put(shareCartSubmitSuccess(res));
  } catch (error) {
    const errorResponse = yield getErrorResponse(error);
    const errors = get(errorResponse, ['errors'], {});
    const errorData = get(errors, ['0'], {});
    yield put(setGlobalErrorStatus({ error: errorData }));
    yield put(shareCartSubmitSuccess(errorResponse));
  }
}

export function* shareConfirmationSubmit(action) {
  try {
    const { data: body } = action;
    const fetchProps = {
      body,
      method: POST,
      credentials: 'include',
      mode: 'cors',
    };
    const options = {
      fetchProps,
      query: {},
      pathVars: getDefaultPathVar(),
      showLoader: true,
    };
    const res = yield call(API.fetch, API_URLS.shareOrder, options);
    const error = get(res, ['errors', '0'], {});
    if (!isEmpty(error)) {
      yield put(setGlobalErrorStatus({ error }));
    }
    yield put(shareConfirmationSubmitSuccess(res));
  } catch (error) {
    const errorResponse = yield getErrorResponse(error);
    const errors = get(errorResponse, ['errors'], {});
    const errorData = get(errors, ['0'], {});
    yield put(setGlobalErrorStatus({ error: errorData }));
    yield put(shareConfirmationSubmitSuccess(errorResponse));
  }
}
export function* loadAccountData() {
  const isUserSignedIn = isUserLoggedIn();
  try {
    if (isUserSignedIn) {
      yield loadCheckoutSaga();
    }
  } catch (error) {
    yield put(setGlobalErrorStatus({ error }));
  }
}
export default function* shoppingCartSaga() {
  yield all([
    takeLatest(LOAD_ACCOUNT_DATA, loadAccountData),
    takeLatest(ADD_ITEM_TO_CART, addItemToCart),
    takeLatest(UPDATE_CART, updateCart),
    takeLatest(REMOVE_CART, removeCart),
    takeLatest(REMOVE_CART_ITEM, removeCartItemSaga),
    takeLatest(REQUEST_QUOTE, requestQuote),
    takeLatest(BULK_ADD_ITEM_TO_CART, bulkAddItemToCart),
    takeLatest(CREATE_REQUISITION, createRequisition),
    takeLatest(VALIDATE_QUOTE_CODE, validateQuoteCode),
    takeLatest(SHARE_CART_SUBMIT, shareCartSubmit),
    takeLatest(SHARE_CONFIRMATION_SUBMIT, shareConfirmationSubmit),
  ]);
}
